import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  Plus,
  Edit,
  CheckCircle,
  XCircle,
  AlertCircle,
  Video,
  Building,
  Briefcase,
  MessageSquare,
  Loader2,
  ChevronDown,
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";
import { AddInterviewModal } from "../../components/reusableComponents/AddInterviewModal";

const STATUS_CONFIG = {
  "En attente": {
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
    icon: <AlertCircle className="w-4 h-4" />,
  },
  "Planifié": {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: <Clock className="w-4 h-4" />,
  },
  "Confirmé": {
    color: "bg-indigo-100 text-indigo-800 border-indigo-200",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  "Terminé": {
    color: "bg-green-100 text-green-800 border-green-200",
    icon: <CheckCircle className="w-4 h-4" />,
  },
  "Annulé": {
    color: "bg-red-100 text-red-800 border-red-200",
    icon: <XCircle className="w-4 h-4" />,
  },
};

const AVAILABLE_STATUSES = Object.keys(STATUS_CONFIG);

const InterviewsSection = ({ candidateId, candidate }) => {
  const [interviews, setInterviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [statusMenuId, setStatusMenuId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const loadInterviews = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`entretien/${candidateId}`);
      setInterviews(res.data);
    } catch (error) {
      console.error("Erreur lors du chargement des entretiens:", error);
    } finally {
      setIsLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const updateStatus = async (entretienId, newStatus) => {
    setUpdatingId(entretienId);
    try {
      await axios.put(`entretien/${entretienId}/update`, { status: newStatus });
      setInterviews((prev) =>
        prev.map((i) =>
          i._id === entretienId ? { ...i, status: newStatus } : i
        )
      );
      toast.success(`Statut mis à jour : ${newStatus}`);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Erreur lors de la mise à jour du statut");
    } finally {
      setUpdatingId(null);
      setStatusMenuId(null);
    }
  };

  const getStatusInfo = (status) =>
    STATUS_CONFIG[status] || {
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: <Clock className="w-4 h-4" />,
    };

  const getTypeIcon = (place) => {
    if (!place) return <Building className="w-5 h-5 text-blue-600" />;
    const lower = place.toLowerCase();
    if (lower.includes("teams") || lower.includes("zoom") || lower.includes("visio")) {
      return <Video className="w-5 h-5 text-blue-600" />;
    }
    if (lower.includes("tel") || lower.includes("phone")) {
      return <Phone className="w-5 h-5 text-blue-600" />;
    }
    return <Building className="w-5 h-5 text-blue-600" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non définie";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const onInterviewCreated = () => {
    setIsAddModalOpen(false);
    loadInterviews();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Entretiens</h2>
          <p className="text-gray-600 mt-1">
            {interviews.length} entretien{interviews.length !== 1 ? "s" : ""} pour ce candidat
          </p>
        </div>
        <motion.button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-4 h-4" />
          <span>Nouvel entretien</span>
        </motion.button>
      </div>

      {interviews.length === 0 ? (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun entretien programmé
          </h3>
          <p className="text-gray-600 mb-4">
            Commencez par planifier un entretien pour ce candidat
          </p>
          <motion.button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            Planifier un entretien
          </motion.button>
        </motion.div>
      ) : (
        <div className="grid gap-6">
          <AnimatePresence>
            {interviews.map((interview, index) => {
              const statusInfo = getStatusInfo(interview.status);
              return (
                <motion.div
                  key={interview._id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        {getTypeIcon(interview.place)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {interview.client?.companyName || "Client non défini"}
                        </h3>
                        {interview.poste && (
                          <p className="text-gray-600 text-sm flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {interview.poste}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status dropdown */}
                    <div className="relative">
                      <button
                        onClick={() =>
                          setStatusMenuId(
                            statusMenuId === interview._id ? null : interview._id
                          )
                        }
                        disabled={updatingId === interview._id}
                        className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-1.5 transition-colors ${statusInfo.color}`}
                      >
                        {updatingId === interview._id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          statusInfo.icon
                        )}
                        <span>{interview.status || "En attente"}</span>
                        <ChevronDown className="w-3 h-3" />
                      </button>

                      {statusMenuId === interview._id && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[160px]">
                          {AVAILABLE_STATUSES.map((status) => (
                            <button
                              key={status}
                              onClick={() => updateStatus(interview._id, status)}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg ${
                                interview.status === status
                                  ? "font-semibold bg-gray-50"
                                  : ""
                              }`}
                            >
                              {STATUS_CONFIG[status].icon}
                              {status}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span>{formatDate(interview.date)}</span>
                    </div>
                    {interview.hour && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span>{interview.hour}</span>
                      </div>
                    )}
                    {interview.place && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>{interview.place}</span>
                      </div>
                    )}
                  </div>

                  {interview.recruteur && (
                    <div className="flex items-center space-x-2 text-gray-600 mb-4">
                      <User className="w-4 h-4 flex-shrink-0" />
                      <span>
                        Recruteur : {interview.recruteur.firstname}{" "}
                        {interview.recruteur.lastname}
                      </span>
                    </div>
                  )}

                  {interview.comment && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700 flex items-start gap-2">
                        <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {interview.comment}
                      </p>
                    </div>
                  )}

                  {interview.cancelreason && (
                    <div className="bg-red-50 rounded-lg p-3 mt-2">
                      <p className="text-sm text-red-700">
                        <strong>Motif d'annulation :</strong>{" "}
                        {interview.cancelreason}
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {isAddModalOpen && candidate && (
        <AddInterviewModal
          isOpen={isAddModalOpen}
          onClose={onInterviewCreated}
          candidate={candidate}
        />
      )}
    </div>
  );
};

export default InterviewsSection;
